import React, {ReactNode, useContext, useEffect, useRef, useState} from "react"
import {
    Grid2,
    CardMedia,
    CardContent,
    Typography,
    TextField,
    Box,
    IconButton,
    Chip,
    FormControl,
    InputLabel,
    Select,
    SelectChangeEvent,
    MenuItem,
    Card
} from "@mui/material"
import {IProduct} from "../models/IProduct.ts"
import {useNavigate} from "react-router-dom"
import {useTranslation} from 'react-i18next';
import {SupportedLanguages} from "../models/Languages.ts";
import OrderContext from "../contexts/OrderContext.tsx";
import {arrayMove, horizontalListSortingStrategy, SortableContext, useSortable} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {closestCenter, DndContext, PointerSensor, useSensor, useSensors} from "@dnd-kit/core";
import ProductContext from "../contexts/ProductContext.tsx";
import DragHandleIcon from '@mui/icons-material/DragHandle';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import AuthContext from "../contexts/AuthContext.tsx";
import {t} from "i18next";
import {useScrollRestoration} from "../functions/useScrollRestoration.ts";

const VITE_API_BASE_URL = import.meta.env.VITE_API_BASE_URL


const SortableItem = ({id, children}: { id: string; children: ReactNode }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({id});

    const {isAdmin} = useContext(AuthContext);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <Grid2 ref={setNodeRef} style={style} size={{xs: 6, sm: 6, md: 4}}>
            <div style={{position: 'relative'}}>
                {/* 🟢 DRAG HANDLE */}
                <div
                    {...attributes}
                    {...listeners}
                    style={{
                        position: 'absolute',
                        top: 8,
                        right: 8,

                        cursor: 'grab',
                        zIndex: 10,
                        touchAction: 'none',
                    }}
                >
                    {isAdmin && <DragHandleIcon/>}
                </div>

                {/* 🟢 Your product card */}
                {children}
            </div>
        </Grid2>
    );
};

interface ProductListProps {
    products: IProduct[]
    category?: string
}

const ProductList: React.FC<ProductListProps> = ({products, category}) => {
    const isDraggingRef = useRef(false);
    const navigate = useNavigate()
    const {i18n} = useTranslation();
    const {
        updateProductMutation,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        setItemsPerPage
    } = useContext(ProductContext)
    const {selectedCurrency, rates} = useContext(OrderContext)
    const {isAdmin} = useContext(AuthContext)
    const currentLanguage: SupportedLanguages = i18n.language as SupportedLanguages
    const userLocale = navigator.language || 'en-US';
    const [sortedProducts, setSortedProducts] = useState<IProduct[]>();
    const [searchTerm, setSearchTerm] = useState('');
    const scrollKey = `productsPageScroll${category ?? ''}`;

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                delay: 200,
                tolerance: 5,
            },
        })
    );

    const filteredProducts = sortedProducts?.filter(product =>
        product.name[currentLanguage].toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

    const paginatedProducts = filteredProducts.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= totalPages) {
            setCurrentPage(newPage);
            window.scrollTo({top: 0, behavior: 'smooth'});
        }
    };

    const getPaginationRange = (current: number, total: number): (number | string)[] => {
        const range: (number | string)[] = [];

        if (total <= 7) {
            for (let i = 1; i <= total; i++) range.push(i);
        } else {
            range.push(1);

            if (current > 4) range.push("...");

            const start = Math.max(2, current - 1);
            const end = Math.min(total - 1, current + 1);
            for (let i = start; i <= end; i++) range.push(i);

            if (current < total - 3) range.push("...");
            range.push(total);
        }

        return range;
    };
    useEffect(() => {
        if (products)
            setSortedProducts(
                isAdmin ?
                    products
                        .sort((a, b) => a.position! - b.position!)
                    : products
                        .filter((product) => product.amount !== 0)
                        .sort((a, b) => a.position! - b.position!)
            )

    }, [products, isAdmin]);


    useEffect(() => {
        const handleScroll = () => {
            sessionStorage.setItem(scrollKey, String(window.scrollY));
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [scrollKey]);

    useScrollRestoration(scrollKey);

    const handleProductClick = (id: string) => {
        if (!isDraggingRef.current) {
            sessionStorage.setItem(scrollKey, String(window.scrollY))
            navigate(`/product/${id}`)
        }
    }

    const handleDragMove = () => {
        isDraggingRef.current = true;
    };

    const handleDragEnd = ({active, over}: any) => {
        isDraggingRef.current = false;

        if (!over) return;

        if (active.id !== over.id) {
            const oldIndex = sortedProducts?.findIndex(p => p._id === active.id);
            const newIndex = sortedProducts?.findIndex(p => p._id === over.id);
            const newSorted = arrayMove(sortedProducts!, oldIndex!, newIndex!);

            const updatedWithPosition: IProduct[] = newSorted.map((product, index) => ({
                ...product,
                position: index + 1,
            }));

            setSortedProducts(updatedWithPosition);

            updatedWithPosition.map(async (product) => {
                await updateProductMutation.mutateAsync(product);
            })
        }
    };

    return (
        sortedProducts &&
        <Box>
            <Grid2
                container
                spacing={2}
                sx={{mb: 2, mt: 2}}
            >
                <Grid2 size={{xs: 12, sm: 8}}>
                    <TextField
                        fullWidth
                        label={t('SEARCH_PRODUCTS_TEXT') || "Search products"}
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1); // reset to page 1 on search
                        }}
                    />
                </Grid2>
                <Grid2 size={{xs: 12, sm: 4}}>

                    <FormControl fullWidth size="small" sx={{backgroundColor: 'white'}}>
                        <InputLabel id="items-per-page-label">
                            {t('ITEMS_PER_PAGE') || "Items per page"}
                        </InputLabel>
                        <Select
                            labelId="items-per-page-label"
                            value={itemsPerPage.toString()}
                            label={t('ITEMS_PER_PAGE') || "Items per page"}
                            onChange={(e: SelectChangeEvent) => {
                                setItemsPerPage(parseInt(e.target.value));
                                setCurrentPage(1);
                            }}
                            slotProps={{
                                root: {
                                    sx: {
                                        fontSize: '0.875rem',
                                        backgroundColor: 'white',
                                    }
                                }
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        backgroundColor: 'white',
                                    }
                                }
                            }}
                        >
                            {[10, 20, 50, 10000].map((value) => (
                                <MenuItem
                                    key={value}
                                    value={value.toString()}
                                    sx={{
                                        fontSize: '0.875rem',
                                        minHeight: '32px',
                                        paddingY: '4px',
                                        paddingX: '12px',
                                    }}
                                >
                                    <Typography color={"black"}>
                                        {value === 10000 ? t('SHOW_ALL') : value}
                                    </Typography>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid2>
            </Grid2>
            <Grid2 container>
                <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd} onDragMove={handleDragMove}
                            sensors={sensors}>
                    <SortableContext items={sortedProducts.filter(product =>
                        product.name[currentLanguage].toLowerCase().includes(searchTerm.toLowerCase()))
                        .map((product) => product._id!)}
                                     strategy={horizontalListSortingStrategy}>
                        {paginatedProducts.map((product) => (
                            <SortableItem id={product._id!} key={product._id}>
                                <Card
                                    onClick={() => handleProductClick(product._id!)}
                                    elevation={4}
                                    sx={{cursor: "pointer", margin: 1, borderRadius: "8px"}}
                                >
                                    {product.images && product.images.length > 0 ? (
                                        <CardMedia
                                            component="img"
                                            loading="lazy"
                                            image={`${VITE_API_BASE_URL}/${product.images[0]}`}
                                            alt={product.name[currentLanguage]}
                                            sx={{borderRadius: "8px"}}
                                        />
                                    ) : (
                                        <CardMedia
                                            component="img"
                                            image="/placeholder-image.jpg"
                                            alt="Placeholder"
                                        />
                                    )}
                                    <CardContent>
                                        <Typography
                                            gutterBottom
                                            variant="body2"
                                            component="div"
                                            sx={{
                                                fontSize: {
                                                    xs: '0.9rem',
                                                    sm: '1rem',
                                                    md: '1.2rem',
                                                    lg: '1.4rem',
                                                },
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                minHeight: '3.2em',
                                            }}
                                        >
                                            {product.name[currentLanguage]}
                                        </Typography>
                                        {product.salePrice !== undefined ? (
                                            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{textDecoration: 'line-through'}}
                                                >
                                                    {new Intl.NumberFormat(userLocale, {
                                                        style: 'currency',
                                                        currency: selectedCurrency,
                                                    }).format(product.price * rates[selectedCurrency])}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    color="error"
                                                    fontWeight="bold"
                                                >
                                                    {new Intl.NumberFormat(userLocale, {
                                                        style: 'currency',
                                                        currency: selectedCurrency,
                                                    }).format(product.salePrice * rates[selectedCurrency])}
                                                </Typography>
                                            </div>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                {new Intl.NumberFormat(userLocale, {
                                                    style: 'currency',
                                                    currency: selectedCurrency,
                                                }).format(product.price * rates[selectedCurrency])}
                                            </Typography>
                                        )}
                                    </CardContent>
                                </Card>
                            </SortableItem>
                        ))}
                    </SortableContext>
                </DndContext>
            </Grid2>
            <Grid2
                container
                justifyContent="center"
                alignItems="center"
                sx={{mt: 2, flexWrap: 'nowrap', overflowX: 'auto'}}>
                <IconButton
                    size="small"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                >
                    <ArrowLeftIcon/>
                </IconButton>

                {getPaginationRange(currentPage, totalPages).map((page, index) =>
                    typeof page === 'number' ? (
                        <Chip
                            key={index}
                            label={page}
                            onClick={() => handlePageChange(page)}
                            size="small"
                            color={currentPage === page ? 'primary' : 'default'}
                            sx={{mx: 0.5}}
                        />
                    ) : (
                        <Chip key={index} label="..." size="small" disabled sx={{mx: 0.5}}/>
                    )
                )}

                <IconButton
                    size="small"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                >
                    <ArrowRightIcon/>
                </IconButton>
            </Grid2>
        </Box>
    )
}

export default ProductList
